/**
 * A room's schedule for the inspection report — the tagged list a drawing carries beside its plan
 * (claude-docs/2026-10-15-inspection-report-items-design.md): the fixed items
 * the as-found room holds, the devices the recording saw, and the furniture,
 * tagged like a drawing's schedule — C1, T1, R1… numbered clockwise from the
 * top of the plan. Returns the rows the list prints and the geometry the plan
 * draws. Pure.
 */
const { CABINET_TYPES, isFixed, isFurniture } = require('./existingObjects');
const { activeReview } = require('./inspectionReview');
const {
  codeLabel,
  materialLabel,
  finishLabel,
  feetAndInches,
} = require('./inspectionLabels');
const { centroid, turnFromTop, roomOutline } = require('./inspectionPlan');

const UPPER_TYPES = new Set(['upper-cabinet', 'wall-cabinet']);

const ITEM_TAGS = {
  storage: 'C',
  countertop: 'CT',
  toilet: 'T',
  sink: 'S',
  bathtub: 'BT',
  shower: 'SH',
  refrigerator: 'A',
  dishwasher: 'A',
  oven: 'A',
  cooktop: 'A',
  range: 'A',
  stove: 'A',
  washerDryer: 'A',
  fireplace: 'F',
};

/**
 * What the floor plan reads to draw an object (`drawObjects` in
 * web-app/src/utils/elementDrawingUtils.js and the shapes it calls): the report
 * freezes these, so its plans draw every fixture exactly as the floor plan
 * view does, and nothing else of the object reaches a shared copy.
 */
const DRAWN_FIELDS = [
  'id',
  'type',
  'category',
  'center',
  'dimensions',
  'rotation',
  'attributes',
  'parents',
  'sinkType',
  'sinks',
  'fixtureGroupId',
  'wallCount',
  'doorType',
  'attachedWallAngles',
  'usePolygonSurround',
  'tub-surround-polygon',
  'tub-cutout',
  'sinkLateralOffsets',
  'polygonCorners',
  'clearanceOffset',
  'stairTurn',
];

/** An object as the plan draws it: its `DRAWN_FIELDS`, the ones it has. */
function drawable(o) {
  const out = {};
  for (const k of DRAWN_FIELDS) if (o[k] !== undefined) out[k] = o[k];
  return out;
}

/** A device's tag letters, by its vocabulary type; anything else is `E`. */
const DEVICE_TAGS = {
  outlet: 'R',
  'gfci-outlet': 'R',
  'outlet-240v': 'R',
  switch: 'SW',
  dimmer: 'SW',
  '3way': 'SW',
  'ceiling-light': 'L',
  'wall-light': 'L',
  'emergency-light': 'L',
  'exhaust-fan': 'FN',
  'ceiling-fan': 'FN',
  'hvac-vent': 'V',
  'return-grille': 'V',
  'smoke-detector': 'D',
  'co-detector': 'D',
  'floor-drain': 'P',
  'shower-valve': 'P',
  'tub-spout': 'P',
  'shower-head': 'P',
  faucet: 'P',
  shower: 'P',
};

/** The order device groups are listed in. */
const DEVICE_ORDER = ['R', 'SW', 'L', 'FN', 'V', 'D', 'P', 'E'];

const GRADE_RANK = { poor: 0, fair: 1, good: 2 };
const cm = (v) => Math.round(v * 100) / 100;
const r3 = (v) => Math.round(v * 1000) / 1000;

const itemTagFor = (type) => (CABINET_TYPES.has(type) ? 'C' : ITEM_TAGS[type]);

/** A door's and a window's tag letters. */
const OPENING_TAGS = { door: 'D', window: 'W' };

/** What kind of door, in words, by the operation the inspection measured. */
const DOOR_KINDS = {
  swing: 'Hinged door',
  'double-swing': 'Double door',
  sliding: 'Sliding door',
  pocket: 'Pocket door',
  bifold: 'Bifold door',
  'overhead-sectional': 'Garage door',
  // no `no-door`: the scan still has a door there, and the apply refuses to turn
  // one into a cased opening, so the report does not claim one either
};

/** What kind of window, in words, by the style the inspection measured. */
const WINDOW_KINDS = {
  'double-hung': 'Double-hung window',
  'single-hung': 'Single-hung window',
  casement: 'Casement window',
  picture: 'Picture window',
  sliding: 'Sliding window',
  bay: 'Bay window',
};

/**
 * A door's or window's name in the schedule: its kind when the inspection
 * measured one ("Pocket door", "Casement window"), "Hinged or pocket door" /
 * "Single- or double-hung window" when nothing seen told those two apart, else "Door" / "Window" — a
 * door measured as no door at all among them, since nothing writes that. A
 * window whose casement-or-sliding call no hardware settled is named by its
 * kind alone — the house decides that call, not the report.
 */
function openingLabel(doc) {
  const t = doc.openingType;
  // A door the model called hinged with no hinge and no open leaf in view: a
  // pocket door pulled shut looks the same, so the report says both
  // (`apply_door_derivations` in the depth service). Not a measurement, so it is
  // read before the verdict gate.
  const hedged = t && t.label && t.label.operationCandidates;
  if (doc.kind === 'door' && Array.isArray(hedged) && hedged.length === 2) {
    const [a, b] = hedged.map((op) =>
      (DOOR_KINDS[op] || op).replace(/ door$/, '')
    );
    return `${a} or ${b.toLowerCase()} door`;
  }
  // A hung window whose upper sash was not seen to move or seen fixed: single-
  // or double-hung, said both ways (`hung_style` in the depth service).
  const hung = t && t.label && t.label.styleCandidates;
  if (doc.kind === 'window' && Array.isArray(hung) && hung.length === 2) {
    return 'Single- or double-hung window';
  }
  const label = t && t.verdict === 'measured' ? t.label || {} : {};
  if (doc.kind === 'door') return DOOR_KINDS[label.operation] || 'Door';
  if (label.styleSettled === false) return 'Window';
  return WINDOW_KINDS[label.style] || 'Window';
}

/**
 * How far into the room a door's or window's tag stands from the opening. At the
 * opening itself the tag sits on the wall line and the door's own symbol and
 * reads badly; a step into the room, along the face normal, clears both.
 */
const OPENING_TAG_INSET_M = 0.3;

/**
 * A unit of doors in words: "Pocket doors, pair", "Hinged or pocket doors, pair".
 * Its leaves are counted by the unit, so a pair of hinged leaves is "Hinged",
 * never "Double" — the pair is said once, at the end.
 */
function unitLabel(doc, count) {
  const one = openingLabel(doc)
    .replace(/^Double door$/, 'Hinged door')
    .replace(/^Double or /, 'Hinged or ');
  const plural = one.replace(/ door$/, ' doors');
  return `${plural}, ${count === 2 ? 'pair' : `${count} doors`}`;
}

/** A door's or window's place on the plan: a step into the room from its middle. */
function openingPoint(doc) {
  const c = doc.anchor && doc.anchor.openingKey && doc.anchor.openingKey.center;
  if (!(c && Number.isFinite(c.x) && Number.isFinite(c.z))) return null;
  const n = doc.anchor.faceNormal;
  const inset =
    n && Number.isFinite(n.x) && Number.isFinite(n.z) ? OPENING_TAG_INSET_M : 0;
  return {
    x: cm(c.x + (inset ? n.x : 0) * inset),
    z: cm(c.z + (inset ? n.z : 0) * inset),
  };
}

/**
 * The doors and windows the inspection looked at, as items: one each, drawn by
 * the plan's own structure layer (so no boxes), tagged at the opening.
 */
function openingItemsOf(docs, surfaceOf) {
  return docs
    .filter((d) => OPENING_TAGS[d.kind])
    .map((doc) => {
      const read = surfaceOf(doc);
      const seen = read.status !== 'not-seen';
      const members = (doc.anchor && doc.anchor.members) || [];
      return {
        key: `opening:${doc.targetId}`,
        letters: OPENING_TAGS[doc.kind],
        // a unit the scan split into several doors is one item, and says so
        label:
          members.length > 1
            ? unitLabel(doc, members.length)
            : openingLabel(doc),
        material: seen ? materialLabel(doc.material) : null,
        finish: seen ? finishLabel(doc) : '',
        grade: seen && read.grade in GRADE_RANK ? read.grade : null,
        status: seen ? 'observed' : 'not-seen',
        targetIds: [doc.targetId],
        boxes: [],
        point: openingPoint(doc),
      };
    });
}

const hasBox = (o) =>
  o &&
  o.center &&
  Number.isFinite(o.center.x) &&
  Number.isFinite(o.center.z) &&
  o.dimensions &&
  Number.isFinite(o.dimensions.x) &&
  Number.isFinite(o.dimensions.z);

/**
 * An object's plan frame: its centre, its local x axis and its local +z (the
 * front, pointing into the room), and half its width and depth — the frame
 * `footprintFor` turns an object by.
 */
function frameOf(o) {
  const ry = o.rotation && Number.isFinite(o.rotation.y) ? o.rotation.y : 0;
  return {
    c: { x: cm(o.center.x), z: cm(o.center.z) },
    ax: { x: r3(Math.cos(ry)), z: r3(-Math.sin(ry)) },
    az: { x: r3(Math.sin(ry)), z: r3(Math.cos(ry)) },
    hw: cm(o.dimensions.x / 2),
    hd: cm(o.dimensions.z / 2),
  };
}

/** The four corners of a frame's rectangle. */
function cornersOf(f) {
  return [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
  ].map(([u, v]) => ({
    x: cm(f.c.x + u * f.hw * f.ax.x + v * f.hd * f.az.x),
    z: cm(f.c.z + u * f.hw * f.ax.z + v * f.hd * f.az.z),
  }));
}

/** Number `list`'s entries per tag letters, clockwise from the top about `centre`. */
function numberClockwise(list, centre) {
  const counts = new Map();
  const turn = (e) => (e.point ? turnFromTop(e.point, centre) : 2 * Math.PI);
  [...list]
    .sort(
      (a, b) => turn(a) - turn(b) || String(a.key).localeCompare(String(b.key))
    )
    .forEach((e) => {
      const n = (counts.get(e.letters) || 0) + 1;
      counts.set(e.letters, n);
      e.tag = `${e.letters}${n}`;
    });
}

/**
 * Where a face end the device's `along` counts from is, as the homeowner reads
 * it: from the LEFT corner as you face the wall from the room. Facing the wall
 * you look along −normal, and your right is (normal.z, −normal.x) (screen x
 * right, z down). Held to the wall: the depth service keeps a device measured
 * up to 15 cm past a face end, which would otherwise read as a negative
 * distance.
 */
function fromLeftCorner(anchor, along) {
  const [a, b] = anchor.faceEnds;
  const n = anchor.faceNormal;
  const right = { x: n.z, z: -n.x };
  const length = Math.hypot(b.x - a.x, b.z - a.z);
  const aIsLeft =
    a.x * right.x + a.z * right.z <= b.x * right.x + b.z * right.z;
  const fromLeft = aIsLeft ? along : length - along;
  return Math.min(Math.max(fromLeft, 0), length);
}

/** Where a device is, in words: "Wall B, 1' 8" from the left corner, 1' 4" up". */
function whereOf(doc, device, wallLetter) {
  if (doc.kind === 'ceiling') return 'Ceiling';
  if (doc.kind === 'floor') return 'Floor';
  if (doc.kind === 'object') {
    const type = (doc.anchor && doc.anchor.type) || 'object';
    return `On the ${codeLabel(type).toLowerCase()}`;
  }
  const letter = wallLetter(doc.targetId);
  const wall = letter ? `Wall ${letter}` : 'Wall';
  const loc = device.location;
  const anchor = doc.anchor;
  if (
    !loc ||
    !Number.isFinite(loc.along) ||
    !Number.isFinite(loc.height) ||
    !anchor ||
    !Array.isArray(anchor.faceEnds) ||
    !anchor.faceNormal
  )
    return wall;
  const fromLeft = feetAndInches(fromLeftCorner(anchor, loc.along));
  // a device measured just below the wall's base is at its foot
  const up = feetAndInches(Math.max(loc.height, 0));
  return `${wall}, ${fromLeft} from the left corner, ${up} up`;
}

/** A wall face's normal into the room, or null when the doc has none. */
function normalOf(doc) {
  const n = doc.anchor && doc.anchor.faceNormal;
  return n && Number.isFinite(n.x) && Number.isFinite(n.z)
    ? { x: r3(n.x), z: r3(n.z) }
    : null;
}

/** A device's place on the plan: its world point, else a ceiling or floor point. */
function pointOf(device) {
  const w = device.world;
  if (w && Number.isFinite(w.x) && Number.isFinite(w.z))
    return { x: cm(w.x), z: cm(w.z) };
  const l = device.location;
  if (l && Number.isFinite(l.x) && Number.isFinite(l.z))
    return { x: cm(l.x), z: cm(l.z) };
  return null;
}

/**
 * The devices of a room's observed docs: never one another surface reported
 * and keeps (`duplicateOf`); a device the contractor hid comes back flagged
 * `hidden`, untagged. A wall the model saw too little of to judge
 * (`model-says-unseen`: cabinets cover most of it) keeps the devices it did
 * see and place — the outlets on its backsplash.
 */
function devicesOf(docs, wallLetter) {
  const out = [];
  for (const doc of docs) {
    if (doc.verdict !== 'observed' && doc.reason !== 'model-says-unseen')
      continue;
    const review = activeReview(doc);
    for (const v of doc.devices || []) {
      if (!v || !v.id || v.duplicateOf) continue;
      out.push({
        key: `${doc.targetId}/${v.id}`,
        targetId: doc.targetId,
        id: v.id,
        letters: DEVICE_TAGS[v.type] || 'E',
        label: v.subtype
          ? `${codeLabel(v.type)}, ${v.subtype}`
          : codeLabel(v.type),
        where: whereOf(doc, v, wallLetter),
        // the letter of the wall it is on, or null off a wall
        wall: doc.kind === 'wall-face' ? wallLetter(doc.targetId) : null,
        point: pointOf(v),
        // what picks its plan symbol: the floor plan's for an electrical type
        // or a light, turned to face out of its wall
        type: v.type,
        subtype: v.subtype || null,
        // what it looks like and, for a fixture, its condition, as the model
        // answered them (`deviceFinishes`, `grades`); null when it was not asked
        finish: v.finish || null,
        grade: v.grade || null,
        normal: doc.kind === 'wall-face' ? normalOf(doc) : null,
        hidden: review.hiddenDevices.includes(v.id),
      });
    }
  }
  return out;
}

/**
 * The fixed objects as items: cabinets of one run and one tier are ONE item (the
 * pipeline's split of a run into boxes is its guess, not the real cabinets), the
 * rest one each. `surfaceOf(doc)` → `{ status, grade }` as the report read it.
 */
function itemsOf(objects, docByElement, surfaceOf) {
  const groups = new Map();
  for (const o of objects) {
    const doc = docByElement.get(o.id) || null;
    const key =
      o.runId && CABINET_TYPES.has(o.type)
        ? `run:${o.runId}:${UPPER_TYPES.has(o.type) ? 'upper' : 'base'}`
        : `object:${o.id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ o, doc });
  }
  return [...groups].map(([key, members]) => {
    const first = members[0].o;
    const run = key.startsWith('run:');
    const read = members.filter((m) => m.doc).map((m) => surfaceOf(m.doc));
    const observed = members.filter(
      (m) => m.doc && surfaceOf(m.doc).status !== 'not-seen'
    );
    const materials = [
      ...new Set(
        observed.map((m) => materialLabel(m.doc.material)).filter(Boolean)
      ),
    ];
    // a run's boxes share a finish or say none: never one box's for the run
    const finishes = [...new Set(observed.map((m) => finishLabel(m.doc)))];
    const grades = observed
      .map((m) => surfaceOf(m.doc).grade)
      .filter((g) => g in GRADE_RANK);
    const boxes = members
      .filter((m) => hasBox(m.o))
      .map((m) => {
        const frame = frameOf(m.o);
        return {
          frame,
          corners: cornersOf(frame),
          object: drawable(m.o),
        };
      });
    let status = 'not-inspected';
    if (observed.length) status = 'observed';
    else if (read.length) status = 'not-seen';
    let label = codeLabel(first.type);
    if (run) {
      label = UPPER_TYPES.has(first.type) ? 'Upper cabinets' : 'Base cabinets';
      if (members.length > 1) label += ` (${members.length})`;
    }
    return {
      key,
      letters: itemTagFor(first.type),
      label,
      material: materials.length > 1 ? 'Mixed' : materials[0] || null,
      finish: finishes.length === 1 ? finishes[0] : '',
      grade: grades.length
        ? grades.reduce((a, b) => (GRADE_RANK[b] < GRADE_RANK[a] ? b : a))
        : null,
      status,
      targetIds: members.filter((m) => m.doc).map((m) => m.doc.targetId),
      boxes,
      point: boxes.length ? centroid(boxes.map((b) => b.frame.c)) : null,
    };
  });
}

/**
 * The schedule of one room. `room`: its as-found room doc (or null — then only
 * devices); `docs`: its existing-conditions docs; `wallLetter(targetId)`: a
 * lettered face's letter; `surfaceOf(doc)`: the report's reading of a doc
 * (`{ status, grade }`, the contractor's grade applied).
 * → { items, devices, furniture, released } — items and visible devices tagged;
 * `released`: the objects the contractor marked not built in, with the letters
 * of their kind.
 */
function roomSchedule({ room, docs, wallLetter, surfaceOf }) {
  const objects = ((room && room.objects) || []).filter(Boolean);
  const byId = new Map(objects.map((o) => [o.id, o]));
  const docByElement = new Map(
    docs
      .filter((d) => d.kind === 'object' && d.elementId)
      .map((d) => [d.elementId, d])
  );
  const notFixed = (o) => {
    const doc = docByElement.get(o.id);
    return Boolean(doc && activeReview(doc).notFixed);
  };
  const fixed = objects.filter((o) => isFixed(o, byId) && !notFixed(o));
  // the objects the contractor says are not built in, for the review page to undo
  const released = objects
    .filter((o) => isFixed(o, byId) && notFixed(o))
    .map((o) => ({
      targetId: docByElement.get(o.id).targetId,
      label: codeLabel(o.type),
      letters: itemTagFor(o.type),
    }));
  const furniture = objects
    .filter(
      (o) => (isFurniture(o) || (isFixed(o, byId) && notFixed(o))) && hasBox(o)
    )
    .map((o) => ({ corners: cornersOf(frameOf(o)) }));

  const items = [
    ...itemsOf(fixed, docByElement, surfaceOf),
    ...openingItemsOf(docs, surfaceOf),
  ];
  const devices = devicesOf(docs, wallLetter);
  const outline = room ? roomOutline(room) : null;
  const points = [...items, ...devices].map((e) => e.point).filter(Boolean);
  const centre = outline
    ? centroid(outline)
    : points.length
      ? centroid(points)
      : { x: 0, z: 0 };
  numberClockwise(items, centre);
  numberClockwise(
    devices.filter((d) => !d.hidden),
    centre
  );
  const order = (d) => DEVICE_ORDER.indexOf(d.letters);
  devices.sort(
    (a, b) =>
      order(a) - order(b) ||
      Number(a.hidden) - Number(b.hidden) ||
      (parseInt((a.tag || '').replace(/\D/g, ''), 10) || 0) -
        (parseInt((b.tag || '').replace(/\D/g, ''), 10) || 0)
  );
  items.sort(
    (a, b) =>
      String(a.letters).localeCompare(String(b.letters)) ||
      parseInt(a.tag.replace(/\D/g, ''), 10) -
        parseInt(b.tag.replace(/\D/g, ''), 10)
  );
  for (const d of devices) if (d.hidden) d.tag = null;
  return { items, devices, furniture, released };
}

module.exports = { roomSchedule, fromLeftCorner, openingLabel, DEVICE_TAGS };
